import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { hrApplicationsByStatus } from '../../lib/jobsApi';
import type { HrStatusRow } from '../../lib/jobsApi';
import { formatRelativeDate } from '../../lib/formatRelativeDate';
import { hrErrorMessage } from '../../lib/tenancyErrorCopy';

/*
 * Velaigal /hr/shortlisted and /hr/rejected — one component parameterized
 * by `status`, backed by hrApplicationsByStatus (a cross-job query: every
 * shortlisted/rejected application in the active company, regardless of which
 * job it's under). Each row links straight to ApplicantDetail
 * (/hr/applications/:id) rather than back to the job — this list exists so
 * a recruiter can jump directly to a candidate they've already actioned.
 *
 * The timestamp is `decidedAt` where there is one, falling back to
 * `stageChangedAt`. Both are now written only by the decision path
 * (server/src/review/decide.ts); until the squash this rendered
 * `statusChangedAt`, which the detail GET stamped on every open — so
 * "Shortlisted 3 days ago" reported when someone last LOOKED. Only the two
 * terminal stages carry `decidedAt`, which is why the fallback is not dead
 * code: a shortlisted row has none.
 */

type ReviewStatus = 'shortlisted' | 'rejected';

const HEADER_COPY: Record<ReviewStatus, { label: string; title: string }> = {
  shortlisted: { label: 'Shortlisted', title: 'Shortlisted candidates' },
  rejected: { label: 'Rejected', title: 'Rejected candidates' },
};

const EMPTY_COPY: Record<ReviewStatus, string> = {
  shortlisted: 'No candidates shortlisted yet.',
  rejected: 'No candidates rejected yet.',
};

const STATUS_VERB: Record<ReviewStatus, string> = {
  shortlisted: 'Shortlisted',
  rejected: 'Rejected',
};

const LoadingBlock: React.FC = () => (
  <div role="status" className="py-16 text-center">
    <span aria-hidden="true" className="velaigal-loader-rule mx-auto w-12 block" />
    <p className="mt-6 text-sm text-ink-tertiary">Loading candidates.</p>
  </div>
);

const ErrorBlock: React.FC<{ message: string; onRetry: () => void }> = ({ message, onRetry }) => (
  <div className="py-16 text-center">
    <span className="label-editorial" style={{ color: 'var(--color-error)' }}>
      Something went wrong
    </span>
    <h2 className="font-display text-2xl font-medium text-ink leading-snug mt-4">Couldn&apos;t load candidates.</h2>
    <p className="mt-4 text-sm text-ink-secondary max-w-md mx-auto leading-relaxed">{message}</p>
    <button type="button" onClick={onRetry} className="btn-secondary mt-8">
      Try again
    </button>
  </div>
);

const EmptyBlock: React.FC<{ status: ReviewStatus }> = ({ status }) => (
  <div className="bg-paper border border-hairline rounded-md p-8 text-center">
    <p className="text-ink">{EMPTY_COPY[status]}</p>
  </div>
);

const StatusRow: React.FC<{ status: ReviewStatus; row: HrStatusRow }> = ({ status, row }) => (
  <Link to={`/hr/applications/${row.id}`} className="card block no-underline text-ink">
    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
      <div className="min-w-0">
        <p className="font-display text-lg font-medium text-ink">{row.candidateName}</p>
        <p className="text-sm text-ink-secondary mt-0.5">{row.candidateEmail}</p>
        <p className="text-xs text-ink-tertiary mt-2">{row.jobTitle}</p>
      </div>
      <span className="text-xs text-ink-tertiary flex-shrink-0">
        {STATUS_VERB[status]} {formatRelativeDate(row.decidedAt ?? row.stageChangedAt)}
      </span>
    </div>
  </Link>
);

interface StatusListProps {
  status: ReviewStatus;
}

const StatusList: React.FC<StatusListProps> = ({ status }) => {
  const [rows, setRows] = useState<HrStatusRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [loadNonce, setLoadNonce] = useState(0);

  // Cancelled-guarded so a fast unmount (or a stale in-flight retry) can't
  // setState afterward — mirrors the load effect in MyJobs.tsx. Re-runs on
  // `status` too, since Shortlisted and Rejected share this one component.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const res = await hrApplicationsByStatus(status);
        if (cancelled) return;
        setRows(res.applications);
      } catch (err) {
        if (cancelled) return;
        setError(hrErrorMessage(err, 'Check your connection and try again.'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [status, loadNonce]);

  const { label, title } = HEADER_COPY[status];

  return (
    <div className="max-w-3xl mx-auto">
      <span className="label-editorial block mb-3">{label}</span>
      <h1 className="font-display text-3xl font-medium text-ink tracking-tight mb-8">{title}</h1>

      {loading ? (
        <LoadingBlock />
      ) : error ? (
        <ErrorBlock message={error} onRetry={() => setLoadNonce((n) => n + 1)} />
      ) : rows.length === 0 ? (
        <EmptyBlock status={status} />
      ) : (
        <div className="space-y-4">
          {rows.map((row) => (
            <StatusRow key={row.id} status={status} row={row} />
          ))}
        </div>
      )}
    </div>
  );
};

export default StatusList;
